import { useEffect, useState } from "react";
import { CalendarClock, Globe, RotateCcw, Save } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import {
  clearTeamHours,
  minutesToTime,
  saveTeamHours,
  timeToMinutes,
  type OperatingDay,
  type OperatingHours,
} from "@/lib/tickets";
import { cn } from "@/lib/utils";
import { ErrorNote } from "./shared";

/**
 * When a team is open, and where that "open" is measured.
 *
 * ⚠️ THE TIMEZONE IS THE PART WORTH READING TWICE. Every other SLA calculation runs in the
 * timezone of the REQUEST'S SITE, because a working day is a local idea — a request raised in
 * Ridgefield is measured against Ridgefield's morning. A team's own hours are the one exception:
 * "IT is open 07:00–19:00" describes when the DESK is staffed, and a team serves all ~29 sites
 * across three timezones, so measuring it against the requester's morning would make one team
 * open at different real instants depending on who asked.
 *
 * ⚠️ Inheriting is NOT "the organisation's timezone" — there isn't one. A team on the
 * organisation's week has each request measured at its own site, which is why the timezone
 * control appears only once the team keeps its own schedule, and why the panel says so in words
 * rather than showing a field with nothing in it.
 *
 * ⚠️ A WEEK IS SAVED AS A WEEK. Seven days in one call, so there is no path that leaves a team
 * holding six of its own days and one of the organisation's.
 */
export function TeamHoursPanel({
  teamId,
  hours,
  onChanged,
}: {
  teamId: string;
  hours: OperatingHours;
  onChanged: () => Promise<void>;
}) {
  const [days, setDays] = useState<OperatingDay[]>(hours.days);
  const [zone, setZone] = useState(hours.timeZoneId ?? "");
  const [zones, setZones] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the server's answer changes — including after a save, so the fields show
  // what was stored rather than what was typed.
  useEffect(() => {
    setDays(hours.days);
    setZone(hours.timeZoneId ?? "");
  }, [hours]);

  useEffect(() => {
    void api<string[]>("/api/me/timezones")
      .then(setZones)
      .catch(() => setZones([]));
  }, []);

  async function run(action: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  function setDay(index: number, patch: Partial<OperatingDay>) {
    setDays((current) => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  }

  return (
    <section className="rounded-lg border border-border">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <h3 className="min-w-0 flex-1 text-sm font-medium">Hours of operation</h3>

        {hours.isOwnSchedule ? (
          <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
            own schedule
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            inherited — organisation week
          </span>
        )}
      </header>

      <div className="space-y-4 p-3">
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {hours.isOwnSchedule ? (
            <span>
              Business-hours targets for this team advance only during these hours, read in{" "}
              <span className="font-medium text-foreground">{hours.timeZoneId}</span> — the
              desk&apos;s own clock, not the requester&apos;s. Targets set to 24/7 ignore this
              entirely.
            </span>
          ) : (
            <span>
              This team follows the organisation&apos;s week, and each request is measured at{" "}
              <span className="font-medium text-foreground">its own site&apos;s</span> local time.
              Give the team its own schedule when the desk is staffed on different hours from the
              organisation as a whole.
            </span>
          )}
        </p>

        {error && <ErrorNote message={error} />}

        <ul className="divide-y divide-border">
          {days.map((d, i) => (
            <li key={d.day} className="flex flex-wrap items-center gap-2 py-2">
              <label className="flex w-36 shrink-0 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={d.isOpen}
                  onChange={(e) => setDay(i, { isOpen: e.target.checked })}
                  className="h-4 w-4"
                />
                {d.day}
              </label>

              {d.isOpen ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="time"
                    aria-label={`${d.day} opens`}
                    value={minutesToTime(d.opensAtMinutes)}
                    onChange={(e) => setDay(i, { opensAtMinutes: timeToMinutes(e.target.value) })}
                    className="h-9 rounded-lg border border-input bg-background px-2 text-base focus:border-ring focus:outline-none md:text-sm"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="time"
                    aria-label={`${d.day} closes`}
                    value={minutesToTime(d.closesAtMinutes)}
                    onChange={(e) => setDay(i, { closesAtMinutes: timeToMinutes(e.target.value) })}
                    className="h-9 rounded-lg border border-input bg-background px-2 text-base focus:border-ring focus:outline-none md:text-sm"
                  />

                  {/* Said at the row rather than only on save — the server refuses it, and
                      finding out after pressing Save is a worse way to learn. */}
                  {d.closesAtMinutes <= d.opensAtMinutes && (
                    <span className="text-xs text-destructive">must close after it opens</span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">Closed</span>
              )}
            </li>
          ))}
        </ul>

        <div>
          <label htmlFor="team-hours-zone" className="mb-1 block text-xs font-medium">
            Measured in
          </label>
          <select
            id="team-hours-zone"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="h-10 w-full max-w-sm rounded-lg border border-input bg-background px-2 text-base focus:border-ring focus:outline-none md:text-sm"
          >
            <option value="">Choose a timezone…</option>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            The desk&apos;s own timezone. Teams span every site, so this is where the team works —
            not where the person who raised the request works.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {hours.isOwnSchedule && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(() => clearTeamHours(teamId), "Those hours could not be reverted.")
              }
              className="mr-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-3 text-sm text-muted-foreground transition hover:border-ring hover:text-foreground disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              Follow the organisation
            </button>
          )}

          <button
            type="button"
            disabled={
              busy || !zone || days.some((d) => d.isOpen && d.closesAtMinutes <= d.opensAtMinutes)
            }
            onClick={() =>
              void run(
                () => saveTeamHours(teamId, { timeZoneId: zone, days }),
                "Those hours could not be saved.",
              )
            }
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50",
            )}
          >
            <Save className="h-4 w-4" aria-hidden />
            {busy ? "Saving…" : hours.isOwnSchedule ? "Save hours" : "Set own hours"}
          </button>
        </div>
      </div>
    </section>
  );
}
